import { useMemo, useRef, useState } from 'react'

interface TagPickerProps {
  value: string[]
  onChange: (next: string[]) => void
  /** Every previously-used tag name, for autocomplete. */
  options: string[]
  placeholder?: string
}

function normalize(tag: string): string {
  return tag.trim()
}

/**
 * Creatable multi-select tag input: shows current tags as removable chips,
 * suggests from `options` while typing, and lets the user add a brand-new
 * tag that isn't in `options` yet. No component library in this codebase
 * (plain bk-* CSS classes), so this is a small hand-rolled combobox rather
 * than e.g. headlessui's Combobox.
 */
export function TagPicker({ value, onChange, options, placeholder = 'Add a tag…' }: TagPickerProps) {
  const [input, setInput] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const selectedLower = useMemo(() => new Set(value.map(v => v.toLowerCase())), [value])

  const suggestions = useMemo(() => {
    const q = input.trim().toLowerCase()
    return options
      .filter(o => !selectedLower.has(o.toLowerCase()))
      .filter(o => (q ? o.toLowerCase().includes(q) : true))
      .slice(0, 20)
  }, [options, input, selectedLower])

  const canCreateNew = input.trim() !== '' && !selectedLower.has(input.trim().toLowerCase())
    && !options.some(o => o.toLowerCase() === input.trim().toLowerCase())

  function addTag(tag: string) {
    const t = normalize(tag)
    if (!t) return
    if (selectedLower.has(t.toLowerCase())) {
      setInput('')
      return
    }
    onChange([...value, t])
    setInput('')
    setActiveIndex(0)
  }

  function removeTag(tag: string) {
    onChange(value.filter(v => v.toLowerCase() !== tag.toLowerCase()))
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const optionCount = suggestions.length + (canCreateNew ? 1 : 0)
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(i => Math.min(i + 1, optionCount - 1))
      setOpen(true)
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(i => Math.max(i - 1, 0))
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      if (activeIndex < suggestions.length && suggestions[activeIndex]) {
        addTag(suggestions[activeIndex])
      } else if (input.trim()) {
        addTag(input)
      }
      return
    }
    if (e.key === 'Backspace' && input === '' && value.length > 0) {
      removeTag(value[value.length - 1])
      return
    }
    if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div className="bk-tag-picker" ref={containerRef}>
      <div className="bk-tag-picker-box">
        {value.map(tag => (
          <span key={tag} className="bk-tag-chip">
            {tag}
            <button
              type="button"
              className="bk-tag-chip-remove"
              onClick={() => removeTag(tag)}
              aria-label={`Remove tag ${tag}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          className="bk-tag-picker-input"
          value={input}
          placeholder={value.length === 0 ? placeholder : ''}
          onChange={e => { setInput(e.target.value); setOpen(true); setActiveIndex(0) }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          onKeyDown={handleKeyDown}
        />
      </div>
      {open && (suggestions.length > 0 || canCreateNew) && (
        <div className="bk-tag-picker-menu">
          {suggestions.map((opt, i) => (
            <div
              key={opt}
              className={`bk-tag-picker-option${i === activeIndex ? ' active' : ''}`}
              onMouseDown={e => { e.preventDefault(); addTag(opt) }}
            >
              {opt}
            </div>
          ))}
          {canCreateNew && (
            <div
              className={`bk-tag-picker-option${activeIndex === suggestions.length ? ' active' : ''}`}
              onMouseDown={e => { e.preventDefault(); addTag(input) }}
            >
              <span>Create “{input.trim()}”</span>
              <span className="bk-tag-picker-hint">new tag</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
